import { createHmac, timingSafeEqual } from 'crypto';

const TRANSPARENT_GIF_BASE64 = 'R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export const MARKETING_TRACKING_PIXEL_GIF = Buffer.from(TRANSPARENT_GIF_BASE64, 'base64');

const BOT_UA_RE =
  /googleimageproxy|yahoo mail|mail\.ru|proofpoint|barracuda|mimecast|outlook-ios\/prefetch|preview|bot|crawler|spider|headless|wget|curl/i;

export type MarketingTrackingKind = 'open' | 'click';

export type MarketingTrackingPayload = {
  k: 'o' | 'c';
  r: string;
  s: string;
  u?: string;
  exp: number;
};

export function isVerifiedMarketingEngagement(userAgent?: string | null): boolean {
  const ua = userAgent?.trim() ?? '';
  if (!ua) return false;
  return !BOT_UA_RE.test(ua);
}

export function signMarketingTrackingToken(payload: MarketingTrackingPayload, secret: string): string {
  const body = Buffer.from(JSON.stringify(payload)).toString('base64url');
  const sig = createHmac('sha256', secret).update(body).digest('base64url');
  return `${body}.${sig}`;
}

export function verifyMarketingTrackingToken(
  token: string,
  secret: string,
): MarketingTrackingPayload | null {
  const [body, sig] = token.split('.');
  if (!body || !sig) return null;
  const expected = createHmac('sha256', secret).update(body).digest('base64url');
  const a = Buffer.from(sig);
  const b = Buffer.from(expected);
  if (a.length !== b.length || !timingSafeEqual(a, b)) return null;
  try {
    const parsed = JSON.parse(Buffer.from(body, 'base64url').toString('utf8')) as MarketingTrackingPayload;
    if (!parsed.r || !parsed.s || (parsed.k !== 'o' && parsed.k !== 'c')) return null;
    if (!parsed.exp || Date.now() > parsed.exp) return null;
    if (parsed.k === 'c' && !parsed.u) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function marketingTrackingOpenUrl(webBaseUrl: string, token: string) {
  return `${webBaseUrl.replace(/\/$/, '')}/api/marketing/public/t/open?t=${encodeURIComponent(token)}`;
}

export function marketingTrackingClickUrl(webBaseUrl: string, token: string) {
  return `${webBaseUrl.replace(/\/$/, '')}/api/marketing/public/t/click?t=${encodeURIComponent(token)}`;
}

function shouldSkipTrackedHref(url: string): boolean {
  const lower = url.toLowerCase();
  return (
    lower.includes('{{') ||
    lower.includes('/account/email-preferences') ||
    lower.includes('/api/marketing/public/t/') ||
    lower.startsWith('mailto:') ||
    lower.startsWith('tel:')
  );
}

export function injectMarketingTrackedHtml(
  html: string,
  input: {
    webBaseUrl: string;
    secret: string;
    recipientId: string;
    sendId: string;
    expiresAtMs: number;
  },
): string {
  const wrapHref = (url: string) => {
    const token = signMarketingTrackingToken(
      { k: 'c', r: input.recipientId, s: input.sendId, u: url, exp: input.expiresAtMs },
      input.secret,
    );
    return marketingTrackingClickUrl(input.webBaseUrl, token);
  };
  const rewritten = html.replace(/href="(https?:[^"]+)"/gi, (match, url: string) => {
    if (shouldSkipTrackedHref(url)) return match;
    return `href="${wrapHref(url)}"`;
  });
  const openToken = signMarketingTrackingToken(
    { k: 'o', r: input.recipientId, s: input.sendId, exp: input.expiresAtMs },
    input.secret,
  );
  const pixel = `<img src="${marketingTrackingOpenUrl(input.webBaseUrl, openToken)}" width="1" height="1" alt="" style="display:none;border:0;width:1px;height:1px"/>`;
  if (/<\/body>/i.test(rewritten)) {
    return rewritten.replace(/<\/body>/i, `${pixel}</body>`);
  }
  return `${rewritten}${pixel}`;
}

export const MARKETING_ANALYTICS_HONESTY_COPY =
  'Raw opens/clicks include prefetch and image-proxy hits. Verified excludes known bot and mailbox-proxy user agents; Apple Mail Privacy still inflates some opens.';
